import { useEffect, type ComponentProps, type ReactNode } from 'react';
import { NJButton, NJIcon, NJIconButton, NJModal, NJModalContentInformation, NJText, NJTooltip } from '@engie-group/fluid-design-system-react';
import type { GridApi, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { Size2MixRightSideModal } from '@shared/size2mix-shell';
import type { CreationMeta } from './types';

/** Nemo bold table title + optional `info` tooltip (e.g. EnergyMixesTable). */
export function TableTitle({ title, tooltip }: { title: string; tooltip?: string }) {
    return (
        <div className="production-contracts-table-title">
            <NJText scale="sm"><strong>{title}</strong></NJText>
            {tooltip && (
                <NJTooltip text={tooltip} hasArrow placement="top">
                    <NJIcon name="info" size="sm" className="production-contracts-info" />
                </NJTooltip>
            )}
        </div>
    );
}

// AG Grid 32 (Nemo) floors each flex column and gives the remainder to the last one;
// AG Grid 31 rounds instead. Reproduce the Nemo distribution so columns line up.
function applyNemoFlexWidths<T>(api: GridApi<T>, viewportWidth: number) {
    const columns = api.getAllDisplayedColumns().filter((column) => !column.isPinned() && column.getColDef().flex);
    if (columns.length === 0 || viewportWidth <= 0) return;
    const fixed = api.getAllDisplayedColumns()
        .filter((column) => !column.isPinned() && !column.getColDef().flex)
        .reduce((sum, column) => sum + column.getActualWidth(), 0);
    const available = viewportWidth - fixed;
    const flexes = columns.map((column) => column.getColDef().flex ?? 1);
    const total = flexes.reduce((sum, flex) => sum + flex, 0);
    const widths = flexes.map((flex) => Math.floor((available * flex) / total));
    widths[widths.length - 1] += available - widths.reduce((sum, width) => sum + width, 0);
    const drift = columns.reduce((sum, column, index) => sum + Math.abs(column.getActualWidth() - widths[index]), 0);
    const changes = columns
        .map((column, index) => ({ key: column, newWidth: widths[index] }))
        .filter(({ key, newWidth }) => key.getActualWidth() !== newWidth && newWidth >= (key.getMinWidth() ?? 0));
    if (changes.length > 0 && drift <= columns.length * 2) api.setColumnWidths(changes);
}

/** `onGridReady` for production tables: keeps Nemo's column width rounding on every resize. */
export function nemoFlexWidths<T>({ api }: GridReadyEvent<T>) {
    let width = 0;
    const apply = () => applyNemoFlexWidths(api, width);
    api.addEventListener('gridSizeChanged', (event: GridSizeChangedEvent<T>) => { width = event.clientWidth; apply(); });
    api.addEventListener('displayedColumnsWidthChanged', () => setTimeout(apply));
}

const pad = (value: number) => String(value).padStart(2, '0');
/** Nemo `formatISODate`: `JJ-MM-AAAA à HH:mm:ss`. */
const formatIsoDate = (iso: string) => {
    const d = new Date(iso);
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} à ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/** Nemo `CreationInfo` (« Créée par … le …, dernière modification … »). */
export function CreationInfo({ data, italic = false }: { data: CreationMeta; italic?: boolean }) {
    const author = data.createdBy ? `${data.createdBy.firstname} ${data.createdBy.lastname}`.trim() : '';
    const creation = data.creationDate ? (author ? `Créée par ${author} le ${formatIsoDate(data.creationDate)}` : `Créée le ${formatIsoDate(data.creationDate)}`) : '';
    const modification = data.modificationDate ? `${creation ? ', ' : ''}dernière modification ${formatIsoDate(data.modificationDate)}` : '';
    if (!creation && !modification) return null;
    return <p className={`production-contracts-creation-info${italic ? ' production-contracts-creation-info--italic' : ''}`}>{creation}{modification}</p>;
}

/** Nemo `MandatoryFieldsNotice`, first line of every create / edit panel. */
export function MandatoryNotice() {
    return <p className="production-contracts-mandatory">Tous les champs avec * sont obligatoires</p>;
}

/** Nemo `ActionsCellRenderer`: tertiary edit / delete icon buttons with tooltips. */
export function EditDeleteActions({ onEdit, onDelete }: { label?: string; onEdit: () => void; onDelete: () => void }) {
    return (
        <div className="production-contracts-actions">
            <NJTooltip text="Modifier" hasArrow placement="top">
                <NJIconButton icon="edit" label="" aria-label="Modifier" variant="tertiary" onClick={onEdit} />
            </NJTooltip>
            <NJTooltip text="Supprimer" hasArrow placement="top">
                <NJIconButton icon="delete" label="" aria-label="Supprimer" variant="tertiary" onClick={onDelete} />
            </NJTooltip>
        </div>
    );
}

export function DeleteConfirmModal({ id, title = 'Confirmation de suppression', children, onCancel, onConfirm, isPending }: {
    id: string;
    title?: string;
    children: ReactNode;
    onCancel: () => void;
    onConfirm: () => void;
    isPending?: boolean;
}) {
    return (
        <NJModal isOpen setIsOpen={(open) => !open && onCancel()} closeOnEscape closeOnBackdropClick>
            <NJModalContentInformation id={id} iconVariant="danger" title={title} centeredFooter shouldFitViewport onClose={onCancel}>
                <div className="production-contracts-modal-text">
                    <NJText scale="md">{children}</NJText>
                </div>
                <NJButton label="Annuler" variant="secondary" className="production-contracts-modal-cancel" onClick={onCancel} />
                <NJButton label="Je supprime" variant="primary" className="production-contracts-modal-confirm" onClick={onConfirm} disabled={isPending} />
            </NJModalContentInformation>
        </NJModal>
    );
}

export const formatValue = (value: unknown) => (value === null || value === undefined || value === '' ? '-' : String(value));

/** AG Grid value parser: empty → null, invalid → keep old value, clamp to ≥ 0. */
export function parseNonNegative({ newValue, oldValue }: { newValue: unknown; oldValue: unknown }) {
    if (newValue === null || newValue === undefined || String(newValue).trim() === '' || String(newValue).trim() === '-') return null;
    const parsed = Number(String(newValue).replace(',', '.').replace('%', ''));
    return Number.isNaN(parsed) ? oldValue : Math.max(0, parsed);
}

export const toNumberOrNull = (text: string) => {
    if (text.trim() === '') return null;
    const parsed = Number(text.replace(',', '.'));
    return Number.isNaN(parsed) ? Number.NaN : parsed;
};

export const inputValue = (event: React.ChangeEvent<HTMLElement>) => (event.target as HTMLInputElement).value;

/** Clicks inside the panel or inside a floating layer it owns (grid editors, tooltips, menus) keep it open. */
const PANEL_OWNED_LAYERS = '.ag-popup, .ag-menu, [role="listbox"], [role="tooltip"], .nj-tooltip';

/** `Size2MixRightSideModal` that also closes on any click outside the panel (backdrop, page, chrome). */
export function RightSidePanel(props: ComponentProps<typeof Size2MixRightSideModal> & { testId: string }) {
    const { isOpen, onClose, testId } = props;
    useEffect(() => {
        if (!isOpen) return undefined;
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Element | null;
            if (!target?.closest || target.closest(`[data-testid="${testId}"]`) || target.closest(PANEL_OWNED_LAYERS)) return;
            onClose();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [isOpen, onClose, testId]);
    return <Size2MixRightSideModal {...props} />;
}
